<template>
  <div
    v-if="$store.state.auth.status.isLoggedIn && !is_loading_resources_in_progress"
    class="relative flex w-full h-screen min-h-screen overflow-hidden transition-all">
    <div v-if="show_team_chat" class="absolute bottom-2 right-2 z-50">
      <TeamChat />
    </div>
    <div
      :style="{ backgroundColor: current_theme?.header_color }"
      class="w-full h-[80px] z-30 bg-white fixed top-0 flex space-x-4 items-center">
      <div
        v-if="$store.state.auth.user.user_type == 'user'"
        class="w-[260px] py-3 pt-0 pl-6 mt-4 mr-5 text-3xl font-semibold text-center text-blue cursor-pointer"
        @click="$router.push('/homepage/home')">
        <div v-if="$store.state.ui_options?.logo" class="flex items-center justify-center max-h-[75px] w-auto">
          <img class="max-h-[75px] w-auto mb-1" :src="$store.state.ui_options?.logo" :alt="$t('Uploaded Image')">
        </div>
        <LogoText v-else :dark-mode="$store.state.ui.darkMode" class="block z-3" />
      </div>

      <div class="w-[200px]">
        <SelectInput
          :items="organizations"
          name="organizations"
          label-attr="name"
          value-attr="id"
          :allow-null="false"
          :show-label="false"
          :label="$t('Switch Organization')"
          class="mt-[2px] w-full flex"
          :value="$store.state.organization.id"
          input-classes="p-5 w-full flex"
          :is-box-background="true"
          :return-ids="true"
          :placeholder="$t('Switch Organization')"
          :organization-selector="true"
          @update:currentItem="(org) => handleSelectOrg(org)" />
      </div>
      <div
        v-if="$store.state.auth.user.user_type == 'user'"
        :class="[hasTripleMenu ? 'w-[calc(100vw-710px)]' : 'w-[calc(100vw-630px)]']"
        class="flex items-center">
        <div class="w-[70%] h-20 mt-6 mr-4">
          <SearchPalette />
        </div>

        <SelectInput
          :items="$store.state.ui_options?.last_viewed_records ? $store.state.ui_options.last_viewed_records : []"
          name="last_viewed"
          label-attr="name"
          value-attr="url"
          :allow-null="false"
          :show-label="false"
          class="mt-[2px]"
          :classes="[`w-1/4 h-full`, { 'w-[22%]': $route.name == 'Dashboard' }]"
          input-classes="p-5"
          :is-box-background="true"
          :return-ids="true"
          :clear-selected-item="true"
          :placeholder="$t('Last visited')"
          :show-object-type="true"
          @update:currentItem="
            (url) => {
              url ? openPageRecord(url) : null
            }
          " />
      </div>
      <Notifications />
      <div class="fixed bottom-0 right-4 overflow-visible">
        <div>
          <AI />
        </div>
      </div>
    </div>

    <div class="fixed z-30 right-4 -top-2">
      <Menu as="div" class="relative inline-block px-3 mt-6 text-left">
        <MenuButton class="w-full px-1 py-2 mb-2 text-sm font-medium text-left text-gray-700 rounded-md group has-tooltip delay-[2500]">
          <span class="flex items-center justify-between w-full">
            <span class="flex items-center justify-between min-w-0 space-x-3">
              <span v-if="$store.state.auth.user.avatar" class="relative">
                <img
                  onerror="this.onerror=null; this.src=window.default_avatar;"
                  class="inline-block rounded-full h-9 w-9 object-cover"
                  :src="$store.state.data.users.find((u) => u.id == $store.state.auth.user.id)?.avatar"
                  alt="">
                <span
                  :class="{
                    'bg-green-400': $store.state.auth.status.live_status == 'on',
                    'bg-gray-400': $store.state.auth.status.live_status == 'off',
                    'bg-red-400': $store.state.auth.status.live_status == 'closed'
                  }"
                  class="absolute bottom-0.5 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white" />
              </span>
              <span v-else class="relative">
                <Avatar :first-name="$store.state.auth.user.first_name" :last-name="$store.state.auth.user.last_name" :radius="35" />
                <span
                  :class="{
                    'bg-green-400': $store.state.auth.status.live_status == 'on',
                    'bg-gray-400': $store.state.auth.status.live_status == 'off',
                    'bg-red-400': $store.state.auth.status.live_status == 'closed'
                  }"
                  class="absolute bottom-0.5 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white" />
              </span>
            </span>
          </span>
          <div class="tooltip bg-gray-800 text-white p-2 rounded-md text-xs right-10 delay-[2500]">
            {{ $store.state.auth.user.full_name }} {{ $store.state.auth.user.email }}
          </div>
        </MenuButton>

        <!-- Sidebar user menu -->
        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0">
          <!-- mx wil have to be adjusted on the "closed" variant to align with where the user picture is -->
          <MenuItems
            class="duration-200 z-20 absolute w-[230px] right-8 -mt-2 mb-6 rounded-md shadow-lg bg-white dark:bg-dark-screen-background ring-1 py-2 ring-blue ring-opacity-5 focus:outline-none">
            <div
              v-for="subItem in filtered_management_navigation"
              :key="subItem.name"
              class="cursor-pointer"
              @click="changeLeftSideTab(subItem)">
              <div class="py-1 hover:bg-gray-300 dark:hover:bg-dark-box-background">
                <MenuItem>
                  <div class="flex">
                    <component
                      :is="subItem.icon"
                      v-if="subItem.icon"
                      class="flex-shrink-0 w-4 h-4 my-auto ml-4 mr-3 text-common-grey"
                      aria-hidden="true" />
                    <Plus v-else :class="'mr-3 flex-shrink-0 h-4 w-4 my-auto ml-4 text-common-grey'" aria-hidden="true" />
                    <div class="text-gray-700 dark:text-dark-body-text">
                      {{
                        subItem.name === $t("Switch to Dark Mode") && $store.state.ui.darkMode
                          ? $t("Switch to Light Mode")
                          : $t(subItem.name)
                      }}
                    </div>
                  </div>
                </MenuItem>
              </div>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>
    <!-- Static sidebar for desktop -->
    <div
      v-if="!main_menu_removed"
      class="relative z-30 mt-[80px] h-full max-h-[calc(100vh-80px)] transition-width duration-500 ease-in-out shadow-md select-none"
      :class="[$store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open ? 'w-[77px]' : 'w-72']">
      <div class="relative flex h-full lg:flex-shrink-0">
        <div
          :style="
            current_theme?.name !== 'Blue Purple Gradient'
              ? {
                backgroundColor:
                  !$store.state.ui.is_sidebar_collapsed && $store.state.ui.side_menu_open && current_theme?.header_color
                    ? current_theme?.header_color
                    : !$store.state.ui.is_sidebar_collapsed && $store.state.ui.side_menu_open && !current_theme?.header_color
                      ? '#ffffff'
                      : ($store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open) ||
                        (!$store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open)
                        ? current_theme.bg_color
                        : current_theme?.header_color
                          ? current_theme.header_color
                          : current_theme.bg_color
              }
              : {}
          "
          class="flex flex-col pt-0 pb-0 pr-0 transition-all ease-in-out duration-10 dark:bg-dark-box-background group w-72"
          :class="[
            {
              'w-[77px]': $store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open,
              'bg-gradient-to-r from-[#2da8d7] to-[#467ddf]': current_theme?.name === 'Blue Purple Gradient'
            }
          ]">
          <div
            :style="current_theme?.name !== 'Blue Purple Gradient' ? { backgroundColor: current_theme.bg_color } : {}"
            :class="[
              {
                '-right-2': $store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open,
                'bg-gradient-to-r from-[#2da8d7] to-[#467ddf]': current_theme?.name === 'Blue Purple Gradient'
              }
            ]"
            class="items-center flex absolute z-50 w-5 transition ease-in-out transform rounded-lg bottom-9 -right-2 h-9 duration-side-menu-transition-duration dark:bg-dark-box-background shadow-c1">
            <ChevronLeft
              v-if="!$store.state.ui.is_sidebar_collapsed"
              class="text-xs text-white cursor-pointer duration-side-menu-transition-duration dark:text-white"
              @click="
                () => {
                  $store.dispatch('ui/setUIValue', {
                    is_sidebar_collapsed: !$store.state.ui.is_sidebar_collapsed,
                    side_menu_open: false
                  })
                }
              " />
            <ChevronRight
              v-if="$store.state.ui.is_sidebar_collapsed"
              class="text-xs text-white cursor-pointer duration-side-menu-transition-duration dark:text-white"
              @click="
                () => {
                  $store.dispatch('ui/setUIValue', {
                    is_sidebar_collapsed: !$store.state.ui.is_sidebar_collapsed,

                    side_menu_open: side_menu_available ? true : false
                  })
                }
              " />
          </div>

          <!-- Sidebar component(opened), swap this element with another sidebar if you like -->
          <div class="flex flex-col overflow-y-auto h-full flex-1">
            <nav
              class="w-[77px] mt-3 absolute z-10 transition-width ease-in-out duration-500 h-full flex flex-col"
              @mouseenter="handleHoverSideMenu(false)"
              @mouseleave="handleHoverSideMenu(true)">
              <draggable v-model="draggable_navigation"
                         tag="div"
                         item-key="name"
                         handle=".drag-handle"
                         @end="onDragEnd">
                <template #item="{ element: menu_item }">
                  <div
                    :class="[menu_item?.classes || '', { 'has-tooltip': hasPopupMenu(menu_item), '!mt-auto': menu_item?.has_separator }]"
                    @mouseenter="menu_item?.name && (hovered_item = menu_item.name)"
                    @mouseleave="menu_item?.name && (hovered_item = null)">
                    <!-- Sidebar items -->
                    <div
                      :class="[
                        getIconTextColor(menu_item),
                        currentTab?.name === menu_item?.name ? 'cursor-default' : 'cursor-pointer',
                        $store.state.ui.is_sidebar_collapsed || $store.state.ui.side_menu_open ? 'px-0 pl-6 w-[77px]' : 'px-0 pl-6 w-72',
                        'flex overflow-hidden whitespace-nowrap items-center h-16 py-4 text-base font-normal antialiased tracking-[0.001em] transform transition-width ease-in-out duration-side-menu-transition-duration'
                      ]"
                      @click="menu_item?.name && changeLeftSideTab(menu_item)">
                      <!-- Sidebar icons -->
                      <component
                        :is="menu_item?.icon"
                        v-if="menu_item?.icon"
                        :width="menu_item?.name === 'Workspace' ? 24 : menu_item?.name === 'Surveys' ? 20 : 22"
                        :height="menu_item?.name === 'Workspace' || menu_item?.name === 'Surveys' ? 24 : 22"
                        :color="getIconColor(menu_item)"
                        :class="[getIconTextColor(menu_item)]"
                        :active="currentTab?.name === menu_item?.name"
                        :dark-mode="$store.state.ui.darkMode"
                        class="relative z-10 flex-shrink-0 w-6 h-6 ml-2 mr-3 drag-handle"
                        aria-hidden="true" />
                      <div
                        v-if="currentTab?.name === menu_item?.name || hovered_item === menu_item?.name"
                        :style="{ backgroundColor: getTwBgColor(menu_item) }"
                        :class="{
                          'rounded-l-full': $store.state.ui.side_menu_open || $store.state.ui.is_sidebar_collapsed
                        }"
                        class="w-[110%] -ml-3 h-12 absolute -mt-1 rounded-full z-0 transition-all duration-500" />
                      <BlueArcVector
                        v-if="currentTab?.name === menu_item?.name || hovered_item === menu_item?.name"
                        :color="getBgColor(menu_item)"
                        class="absolute -top-[3px] -right-[1.5px] scale-200 transition-all duration-500" />
                      <BlueArcVector2
                        v-if="currentTab?.name === menu_item?.name || hovered_item === menu_item?.name"
                        :color="getBgColor(menu_item)"
                        class="absolute bottom-[1px] -right-[1.5px] transition-all duration-500 scale-200" />

                      <!-- Active left side div for highlighting -->
                      <div :class="'ml-4'" @click="menu_item?.name && changeLeftSideTab(menu_item)">
                        <div class="flex flex-col">
                          <span
                            v-if="!$store.state.ui.side_menu_open && !$store.state.ui.is_sidebar_collapsed"
                            class="relative z-10 text-lg transition-opacity duration-200 ease-in-out">
                            {{ menu_item?.label ? $t(menu_item.label) : $utils.splitCamelCase(menu_item?.name) }}
                          </span>
                          <span v-if="!menu_item?.is_activated" class="text-sm">{{ $t("Get access") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </nav>
          </div>

          <div
            :style="current_theme?.name !== 'Blue Purple Gradient' ? { backgroundColor: current_theme.bg_color } : {}"
            class="ml-[76px] z-0 overflow-visible h-[calc(100vh-80px)] w-[215px]"
            :class="[
              !$store.state.ui.side_menu_open ? '!w-0' : 'opacity-100',
              {
                'bg-gradient-to-r from-[#2da8d7] to-[#467ddf]': current_theme?.name === 'Blue Purple Gradient'
              }
            ]">
            <SideMenu
              :side-menu-open="$store.state.ui.side_menu_open"
              :journeys-objects="journeys_objects"
              :change-tab="changeLeftSideTab"
              :current-item="current_item" />
          </div>

          <router-link
            v-if="!(isModuleActivated('portfolio') || isModuleActivated('prospecting'))"
            :to="{ name: 'IntegrationsList' }"
            class="flex items-center justify-center px-3 py-2 ml-2 duration-500 ease-in-out bg-white border rounded-md dark:bg-dark-screen-background">
            <LockOpen :color="$store.state.ui.darkMode ? '#fff' : '#899399'" class="border-none w-9 h-9 dark:text-gray-500" />
            <p class="ml-3 dark:text-white">
              {{ $t("Upload customers to unlock more modules") }}
            </p>
          </router-link>
        </div>
        <div v-show="false" class="absolute w-[90%] h-[200px] rounded-r-3xl z-20 bottom-10 bg-gradient-to-r from-[#2da8d7] to-[#467ddf]">
          <UserRoundPlus class="absolute -z-10 bottom-2 right-5 w-36 h-36 text-white opacity-10" />
          <div class="flex flex-col w-full h-full px-8 py-5">
            <UserRoundPlus class="border-none w-16 h-16 text-white dark:text-gray-500" />
            <span class="text-white">{{ $t("Increase your speed with team members") }}</span>
            <button class="font-normal btn cancel-btn ml-0 w-fit mt-1 rounded-lg border-dark-brand-blue">
              <UserRoundPlus color="#2da8d7" class="w-5 h-5 text-white" />
              <span class="pl-3 bg-gradient-to-r from-[#2da8d7] to-[#467ddf] bg-clip-text text-transparent">
                {{ $t("Invite team members") }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Main column -->
    <div
      :class="[
        {
          'ml-4': $store.state.ui.is_sidebar_collapsed && !$store.state.ui.side_menu_open
        }
      ]"
      class="w-full h-full pt-20 overflow-hidden focus:outline-none">
      <router-view v-slot="{ Component }">
        <keep-alive
          :exclude="['Forecasting', 'RevenueContainer', 'ExpansionContainer', 'AdminContainer', 'CustomerInbox', 'IntegrationsList']">
          <component :is="Component" :key="$route.fullPath" />
        </keep-alive>
      </router-view>
    </div>

    <!-- Modals -->
    <Modals />
    <GenericNewRecordModal />
  </div>
  <div v-else>
    <LoadingProgressBar :percent="resources_loading_percentage" />
  </div>
</template>

<script>
import TeamChat from "@/components/shared/TeamChat.vue"
import SideMenu from "./SideMenu.vue"
import store from "@/store"
import AI from "@/components/shared/ai"
import { markRaw } from "vue"
import { Dialog, DialogOverlay, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from "@headlessui/vue"
import draggable from "vuedraggable"
import {
  Wrench,
  UserRoundPlus,
  LogOut,
  Building2,
  Receipt,
  Settings,
  UsersRound,
  FolderInput,
  ChartNoAxesColumnDecreasing,
  Gem,
  PencilRuler,
  ListTodo,
  ChartNoAxesCombined,
  ChevronRight,
  Plus,
  ChevronLeft,
  Cog,
  Mail,
  Play,
  LockOpen,
  CircleHelp,
  MessageCircle,
  LayoutDashboard,
  FolderOpen,
  House,
  ChartPie,
  Columns3,
  Sun,
  Moon
} from "lucide-vue-next"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue"
import LogoText from "@/components/shared/LogoText.vue"
import Logo from "@/components/shared/Logo.vue"
import Notifications from "@/views/dashboard/Notifications.vue"
import BlueArcVector from "@/components/shared/icons/BlueArcVectorTopRight.vue"
import BlueArcVector2 from "@/components/shared/icons/BlueArcVectorBottomRight.vue"
import Spinner from "@/components/shared/LoadingSpinner.vue"
import LoadingProgressBar from "@/components/shared/LoadingProgressBar.vue"
import Modals from "@/views/dashboard/Modals.vue"
import Avatar from "@/components/shared/Avatar.vue"
import GenericNewRecordModal from "@/views/dashboard/GenericNewRecordModal.vue"
import SearchPalette from "@/views/dashboard/SearchPalette.vue"
import { utils } from "@/store/helpers"
import FadeTransition from "@/components/shared/transitions/FadeTransition.vue"
import dataService from "@/services/data.service"
import SelectInput from "@/components/shared/SelectInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { event as google_event } from "vue-gtag-next"

export default {
  components: {
    Notifications,
    AI,
    TeamChat,
    CircleHelp,
    MessageCircle,
    FadeTransition,
    BlueArcVector,
    BlueArcVector2,
    SelectInput,
    SearchPalette,
    LockOpen,
    LoadingProgressBar,
    Avatar,
    Modals,
    Dialog,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Play,
    Cog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    ChevronRight,
    ChevronLeft,
    Plus,
    LogoText,
    Logo,
    Spinner,
    Gem: markRaw(Gem),
    UserRoundPlus: markRaw(UserRoundPlus),
    ChartNoAxesColumnDecreasing: markRaw(ChartNoAxesColumnDecreasing),
    LayoutDashboard: markRaw(LayoutDashboard),
    House: markRaw(House),
    ListTodo: markRaw(ListTodo),
    ChartNoAxesCombined: markRaw(ChartNoAxesCombined),
    PencilRuler: markRaw(PencilRuler),
    Columns3: markRaw(Columns3),
    FolderOpen: markRaw(FolderOpen),
    FolderInput: markRaw(FolderInput),
    UsersRound: markRaw(UsersRound),
    Wrench: markRaw(Wrench),
    Receipt: markRaw(Receipt),
    Settings: markRaw(Settings),
    LogOut: markRaw(LogOut),
    ChartPie: markRaw(ChartPie),
    Mail: markRaw(Mail),
    Sun: markRaw(Sun),
    Moon: markRaw(Moon),
    GenericNewRecordModal,
    SideMenu,
    draggable
  },
  inject: ["getModule", "isModuleActivated"],
  provide: {
    updateCursorPosition(event) {
      utils.pages.updateCursorPosition(event)
    },

    getViewMetrics: ({
      page_reference,
      object_type,
      view_type,
      current_forecast_name,
      flat = true,
      layout_page_reference,
      component_reference
    }) => {
      return utils.pages.getViewMetrics({
        page_reference,
        object_type,
        view_type,
        current_forecast_name,
        flat,
        layout_page_reference,
        component_reference
      })
    },

    setViewMetrics: ({
      page_reference,
      object_type,
      view_type,
      current_forecast_name,
      new_metrics_list,
      layout_page_reference,
      component_reference
    }) => {
      utils.pages.setViewMetrics({
        page_reference,
        object_type,
        view_type,
        current_forecast_name,
        new_metrics_list,
        layout_page_reference,
        component_reference
      })
    },
    removeMetricsFromView: (reference_name) => {
      utils.pages.removeMetricsFromView(reference_name)
    },
    removeObjectTypeFromView: (reference_name) => {
      utils.pages.removeObjectTypeFromView(reference_name)
    },
    getPageTabs: ({ page_reference: page_reference, flat = false }) => {
      return utils.pages.getPageTabs({ page_reference: page_reference, flat: flat })
    },
    setPageTabs: ({ page_reference, new_tabs_list }) => {
      utils.pages.setPageTabs({ page_reference, new_tabs_list })
    },

    getPageGraphs: ({ page_reference: page_reference, object_type: object_type, flat = false }) => {
      return utils.pages.getPageGraphs({ page_reference: page_reference, object_type: object_type, flat: flat })
    },
    setPageGraphs: ({ page_reference, object_type, new_graphs_list }) => {
      utils.pages.setPageGraphs({ page_reference, object_type, new_graphs_list })
    },
    getPageLayout: ({ page_reference: page_reference, object_type: object_type }) => {
      return utils.pages.getPageLayout({ page_reference: page_reference, object_type: object_type })
    },
    setPageLayout: (new_layout) => {
      utils.pages.setPageLayout(new_layout)
    },

    updateLayouts: (layout, module_ref) => {
      return utils.pages.updateLayouts(layout, module_ref)
    },
    deletePageLayout: (existing_layout) => {
      return utils.pages.deletePageLayout(existing_layout)
    },
    sharePageWithTeamMember: ({ page: page, users: target_users }) => {
      return utils.pages.sharePageWithTeamMember({ page: page, users: target_users })
    },
    clonePageToTeamMember: ({ page: page, users: target_users }) => {
      return utils.pages.clonePageToTeamMember({ page: page, users: target_users })
    },
    canUserEditLayout: (user, layout) => {
      return utils.pages.canUserEditLayout(user, layout)
    },
    addPageGraph: ({ page_reference, object_type, graph }) => {
      return utils.pages.addPageGraph({ page_reference, object_type, graph })
    }
  },
  data() {
    return {
      organizations: [],
      show_team_chat: false,
      current_theme: {
        name: "Default",
        bg_color: "#07455d",
        header_color: "#FFFFFF",
        selected_item_color: "#579FBB",
        button_color: "#0D8ABA"
      },
      hovered_item: null,
      main_menu_removed: false,
      key_timeout: null,
      key_log: [],
      side_menu_open_calc: false,
      current_item: null,
      journeys_objects: {},
      navigation: [
        {
          name: this.$t("Homepage"),
          href: "/homepage/",
          icon: House,
          current: false
        },
        {
          name: this.$t("Portfolio"),
          href: `/portfolio/`,
          icon: FolderOpen,
          current: false
        },
        {
          name: this.$t("Dashboard"),
          href: `/dashboard/`,
          icon: LayoutDashboard,
          current: false
        },

        {
          name: this.$t("Customer Inbox"),
          href: "/customer-inbox",
          icon: Mail,
          current: false
        },
        {
          name: this.$t("Prospecting"),
          href: "/prospecting/",
          icon: ChartPie,
          current: false
        },
        {
          name: this.$t("Customer Journeys"),
          href: "/expansion/journeys/",
          icon: Columns3,
          current: false
        },
        {
          name: this.$t("Forecasting"),
          href: "/revenue/forecast/",
          icon: ChartNoAxesCombined,
          current: false
        },
        {
          name: this.$t("Surveys"),
          href: "/surveys",
          icon: ListTodo,
          current: false
        },
        {
          name: this.$t("Playbooks"),
          href: "/playbooks",
          icon: Play,
          current: false
        },
        {
          name: this.$t("Workspace"),
          href: "/workspace",
          icon: PencilRuler,
          current: false
        }
      ],

      management_navigation: [
        {
          name: this.$t("Global Settings"),
          href: null,
          icon: Cog,
          current: false,

          children: [
            {
              name: this.$t("Metrics"),
              href: "/admin/metrics",
              icon: ChartNoAxesColumnDecreasing,
              current: false
            },

            {
              name: this.$t("Scores"),
              href: "/admin/scores",
              icon: ChartNoAxesColumnDecreasing,
              current: false
            },
            {
              name: this.$t("Integrations & Data"),
              href: "/admin/integrations",
              icon: FolderInput,
              current: false
            },
            {
              name: this.$t("Teams"),
              href: "/admin/teams",
              icon: UsersRound,
              current: false
            },
            {
              name: this.$t("Admin Tools"),
              href: "/admin-page",
              icon: Wrench,
              current: false
            },
            {
              name: this.$t("Plans & Billing"),
              href: "/settings/billing",
              icon: Receipt,
              current: false
            },
            {
              name: this.$t("Settings"),
              href: "/settings",
              icon: Settings,
              current: false
            },
            {
              name: this.$t("Sign Out"),
              action: "sign-out",
              icon: LogOut,
              current: false
            }
          ]
        }
      ],

      windowWidth: window.innerHeight,

      is_loading_resources_in_progress: true,
      resources_loading_percentage: 0
    }
  },
  computed: {
    hasTripleMenu() {
      const pages_with_no_menu = [
        "CustomerInbox",
        "Settings",
        "Metrics",
        "Teams",
        "AdminPage",
        "IntegrationsList",
        "Profile",
        "Billing",
        "Forecasting",
        "Playbooks",
        "PlaybookEditor",
        "WorkspacesList"
      ]
      if (pages_with_no_menu.includes(this.$route.name)) {
        return false
      }
      return true
    },
    currentTab() {
      if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
        return { name: "Portfolio" }
      }
      return this.current_item
    },

    side_menu_available() {
      let side_menu_available = false
      side_menu_available = this.current_item?.children?.filter(
        (c) => c?.is_visible && c?.is_activated && c?.entry_point == "parent"
      )?.length
      if (
        this.current_item?.name === "Homepage" ||
        this.current_item?.name === "Prospecting" ||
        this.current_item?.name === "Customer Journeys" ||
        this.current_item?.name === "Portfolio" ||
        this.current_item?.name === "Dashboard" ||
        this.current_item?.name === "Forecasting"
      ) {
        side_menu_available = true
      }
      return side_menu_available
    },
    filtered_navigation() {
      const all_menus = this.$store.state.control.primary_menu.filter((m) => m.is_visible)

      let menus = this.navigation
        .filter((n) =>
          this.$store.state.auth?.user?.user_type == "end_user"
            ? n.name == "Workspace"
            : all_menus.findIndex((x) => x.name.toLowerCase() == n.name.toLowerCase()) !== -1
        )
        .map((n) => {
          let page_module = all_menus.find((x) => x.name.toLowerCase() == n.name.toLowerCase()) || {}
          return { ...n, ...page_module }
        })

      const saved_order = this.$store.state.ui_options?.primary_menu
      if (saved_order) {
        menus.sort((a, b) => {
          const indexA = saved_order.findIndex((item) => item.name === a.name)
          const indexB = saved_order.findIndex((item) => item.name === b.name)
          return indexA - indexB
        })
      }
      return menus
    },

    draggable_navigation: {
      get() {
        return this.filtered_navigation
      },
      set(newValue) {
        this.navigation = newValue
        this.$store.dispatch("ui/setUIValue", {
          primary_menu: newValue
        })
      }
    },

    filtered_management_navigation() {
      let tabs = [
        ...this.management_navigation[0].children
          .filter((n) => this.$store.state.control.actions_menu.findIndex((x) => x.name.toLowerCase() == n.name.toLowerCase()) != -1)
          .map((n) => {
            let r = {
              ...n,
              ...this.$store.state.control.actions_menu.find((x) => x.name.toLowerCase() == n.name.toLowerCase())
            }
            if ("children" in r) {
              delete r["children"]
            }
            return r
          })
      ]

      // Add the "Switch to Dark Mode" item with the correct icon
      // tabs.splice(tabs.length - 1, 0, {
      //   name: this.$t("Switch to Dark Mode"),
      //   action: "switch-to-dark-mode",
      //   href: "",
      //   icon: this.$store.state.ui.darkMode ? Moon : Sun, // Dynamically set the icon here
      //   current: false
      // })

      tabs.splice(tabs.length - 1, 0, {
        name: this.$t("Plans & Billing"),
        action: "billing",
        href: "/settings/billing",
        icon: Receipt
      })

      if (this.$store.state.auth.user.user_type == "end_user" && this.$store.state.data.workspace_organizations.length > 1) {
        tabs.unshift({
          name: this.$t("Change Organization"),
          action: "change-organization",
          icon: Building2
        })
      }
      tabs.push({
        name: this.$t("Share Feedback"),
        action: "share-feedback",
        icon: MessageCircle
      })

      return this.$store.state.auth.user.user_type == "end_user"
        ? tabs.filter((t) => t.action == "switch-to-dark-mode" || t.action == "sign-out" || t.action == "change-organization")
        : tabs
    }
  },
  watch: {
    "$store.state.ui_options.theme": {
      deep: true,
      handler() {
        var bodyStyles = document.body.style
        if (!this.$store.state.ui_options?.theme) {
          this.current_theme = {
            name: "Default",
            bg_color: "#07455d",
            header_color: "#FFFFFF",
            selected_item_color: "#579FBB",
            button_color: "#0D8ABA"
          }
        } else {
          this.current_theme = this.$store.state.ui_options.theme
        }
        this.$store.dispatch("ui/setUIValue", { theme: this.current_theme })
        bodyStyles.setProperty("--background-color", this.current_theme.button_color)
        bodyStyles.setProperty("--selected-item-color", this.current_theme.selected_item_color)
        bodyStyles.setProperty("--button-color", this.current_theme.button_color)
        bodyStyles.setProperty("--header-color", this.current_theme.header_color)

        // Apply theme class to body for gradient support
        this.applyThemeClass()
      }
    },
    side_menu_available() {
      if (!this.side_menu_available) {
        this.side_menu_open_calc = false
      } else {
        this.side_menu_open_calc = true
      }
    },
    $route() {
      let route_name = this.$route.name.toLowerCase()
      if (route_name.includes("workspace")) {
        route_name = "workspace"
      }
      if (this.$store.state.auth.user.user_type == "end_user") {
        setTimeout(() => {
          this.main_menu_removed = true
        }, 1200)
      } else {
        this.main_menu_removed = false
      }
      this.current_item = this.$store.state.control.primary_menu.find((m) => m.name.toLowerCase() == route_name)
      if (!this.current_item && this.$route.name == "Homepage") {
        this.current_item = {
          name: "Homepage",
          href: "/homepage/",
          icon: House,
          reference_code: "rs-homepage",
          entry_point: "primary_menu"
        }
      }
    },
    side_menu_open_calc() {
      this.$utils.debounce(
        () => {
          if (this.side_menu_open_calc != this.$store.state.ui.side_menu_open) {
            this.$store.dispatch("ui/setUIValue", {
              side_menu_open: this.side_menu_open_calc
            })
          }
        },
        this.$store.state.ui.side_menu_open ? 1000 : 200
      )
    },

    windowWidth(newWidth) {
      if (newWidth < 768 && !this.$store.state.ui.is_sidebar_collapsed) {
        this.$store.dispatch("ui/setUIValue", {
          is_sidebar_collapsed: newWidth < 768
        })
      }
    },
    filtered_navigation: {
      immediate: true,
      handler(newVal) {
        const currentItem = newVal.find((n) => n.href === this.$route.path)
        if (currentItem) {
          this.current_item = currentItem
        } else if (this.$route.name === "Homepage") {
          this.current_item = {
            name: "Homepage",
            href: "/homepage/",
            icon: House,
            reference_code: "rs-homepage",
            entry_point: "primary_menu"
          }
        } else {
          this.current_item = null // Safeguard against undefined
        }
      }
    },
    currentTab() {
      setTimeout(() => {
        if (!this.side_menu_available) {
          this.side_menu_open_calc = false
        } else {
          this.side_menu_open_calc = true
        }
      }, 100)
    }
  },
  created() {
    let route_name = this.$route.name.toLowerCase()
    if (route_name.includes("workspace")) {
      route_name = "workspace"
    }
    if (this.$store.state.auth.user.user_type == "end_user") {
      this.main_menu_removed = true
    }

    this.init_realtime_listener()

    // fake increase in resources_loading_percentage in 1s intervals, to show the progress bar
    let interval = setInterval(() => {
      if (this.resources_loading_percentage < 100) {
        this.resources_loading_percentage += 20
      } else {
        clearInterval(interval)
      }
    }, 50)

    dataService
      .getOrganizations()
      .then((res) => {
        this.organizations = res.data.filter((org) => org.users.find((user) => user.id == this.$store.state.auth.user.id))

        if (!this.organizations.length) {
          this.$router.replace("/onboarding")
        }

        const default_organization_id =
          this.organizations.find((o) => o.id == localStorage.getItem("active_organization_id"))?.id || this.organizations[0].id
        this.handleSelectOrg(default_organization_id)

        this.$store.dispatch("getInitialResources").then((resources) => {
          let init_ui_values = {
            language: resources.portfolio.ui_options?.language || "en",
            theme: resources.portfolio.ui_options?.theme || "Default",
            darkMode: resources.portfolio.ui_options?.darkMode || false,
            side_menu_open: true
          }

          this.$store.dispatch("ui/switchLanguage", resources.portfolio.ui_options?.language || "en")

          this.distributeJourneys(resources.funnels)
          this.is_loading_resources_in_progress = false
          this.resources_loading_percentage = 100
          this.current_item = this.filtered_navigation.find((m) => m.name.toLowerCase() == route_name)

          if (!this.current_item && this.$route.name == "Homepage") {
            this.current_item = {
              name: "Homepage",
              href: "/homepage/",
              icon: House,
              reference_code: "rs-homepage",
              entry_point: "primary_menu"
            }
          }
          if (!this.side_menu_available) {
            init_ui_values.side_menu_open = false
          } else {
            init_ui_values.side_menu_open = true
          }
          this.$store.dispatch("ui/setUIValue", init_ui_values)
        })
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          this.$store.dispatch("auth/logout", this.$router)
        }
      })
    // ctrl+k for search palette
    window.addEventListener(
      "keydown",
      (e) => {
        if (this.$route.fullPath.includes("/admin/metrics")) {
          this.$emitter.emit(ALL_EMITTERS.TRIGGER_FORMULA_CLICK, e)
        }
        clearTimeout(this.key_timeout) // clear the timeout

        // Add key to the log
        this.key_log.push(e.key?.toLowerCase())

        // Only keep the last 3 keys
        if (this.key_log.length > 2) {
          this.key_log.shift()
        }

        // Check if the last three keys were  "n", "m"
        if (this.key_log.join("") === "nm" && (e.altKey || e.ctrlKey)) {
          this.$emitter.emit(ALL_EMITTERS.CREATE_CUSTOM_METRIC, {
            view_type: "table"
          })
          e.preventDefault()
        } else if ((e.altKey || e.ctrlKey) && e.keyCode === 75) {
          this.$store.commit("ui/toggleSearchPalette", true)
          e.preventDefault()
        }
        // ctrl+u for add/remove components
        else if (
          (e.altKey || e.ctrlKey) &&
          e.keyCode === 85 &&
          (this.$route.name == "WorkspaceItem" ? this.$store.state.ui.workspace_user_access_scope == "admin" : true)
        ) {
          this.$emitter.emit(ALL_EMITTERS.TOGGLE_ADD_REMOVE_COMPONENT, {
            selected_component: null,
            step: null
          })
          e.preventDefault()
        }
        // ctrl+r for add/remove metrics
        else if ((e.altKey || e.ctrlKey) && e.keyCode === 82) {
          this.$emitter.emit(ALL_EMITTERS.TOGGLE_ADD_REMOVE_COMPONENT, {
            selected_component: "rollup",
            step: 2
          })
          e.preventDefault()
        }

        // ctrl+i for edit layout
        else if (
          (e.altKey || e.ctrlKey) &&
          e.keyCode === 73 &&
          (this.$route.name == "WorkspaceItem" ? this.$store.state.ui.workspace_user_access_scope == "admin" : true)
        ) {
          this.$emitter.emit(ALL_EMITTERS.TOGGLE_EDIT_LAYOUT)
          e.preventDefault()
        }
        // ctrl+d for edit dublicate
        else if ((e.altKey || e.ctrlKey) && e.keyCode === 68 && this.$route.name !== "WorkspaceItem") {
          this.$emitter.emit(ALL_EMITTERS.DUPLICATE_PAGE)
          e.preventDefault()
        }

        // If no key is pressed for one second, clear the log
        this.key_timeout = setTimeout(() => {
          this.key_log = []
        }, 1000)
      },
      true
    )

    if (this.$route.name == "IntegrationsList" || this.$route.name == "SalesforceIntegration" || this.$route.name == "Teams") {
      store.dispatch("ui/setCurrentSelectedTab", "Integrations")
    } else {
      store.dispatch("ui/setCurrentSelectedTab", this.$route.name)
    }
  },

  mounted() {
    this.register_listeners()
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize)
    })
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.onResize)
    this.unregister_listeners()
  },

  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    applyThemeClass() {
      // Remove existing theme classes
      const existingThemeClasses = ["theme-aubergine", "theme-clementine", "theme-jade", "theme-lagoon", "theme-blue-purple", "theme-dark"]
      existingThemeClasses.forEach((className) => {
        document.body.classList.remove(className)
      })

      // Apply current theme class based on theme name
      if (this.current_theme?.name) {
        const themeClassName = `theme-${this.current_theme.name.toLowerCase().replace(/\s+/g, "-")}`
        document.body.classList.add(themeClassName)
      }
    },
    handleSelectOrg(organization_id) {
      let should_reload = organization_id != localStorage.getItem("active_organization_id")
      let current_org = this.organizations.find((org) => org.id == organization_id)
      this.$store.commit("setOrganization", current_org)
      localStorage.setItem("active_organization_id", current_org.id)
      if (should_reload) {
        window.location.reload()
      }
    },

    openPageRecord(url) {
      if (url[url.length - 1] == "/") {
        url = url.slice(0, -1)
      }
      if (!url.startsWith("/portfolio")) {
        url = "/portfolio" + url
      }
      this.$router.push(url)
    },

    getIconColor(item) {
      let current_tab = this.currentTab?.name
      if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
        current_tab = "Portfolio"
      }

      if (this.$store.state.ui.side_menu_open) {
        if (current_tab == item.name || !this.currentTab) {
          return "#FFFFFF"
        }
        return this.hovered_item == item.name ? "#FFFFFF" : "#07455d"
      } else {
        if (this.hovered_item == item.name && current_tab != item.name) {
          return "#FFFFFF"
        }
        return "#FFFFFF"
      }
    },
    getIconTextColor(item) {
      let current_tab = this.currentTab?.name
      if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
        current_tab = "Portfolio"
      }

      if (this.$store.state.ui.side_menu_open) {
        if (current_tab == item.name) {
          return "text-[#FFFFFF]"
        }
        return this.hovered_item == item.name ? "text-[#FFFFFF]" : "text-[#07455d]"
      } else {
        if (this.hovered_item == item.name && current_tab != item.name) {
          return "text-[#FFFFFF]"
        }
        return "text-[#FFFFFF]"
      }
    },

    getBgColor(item) {
      let current_tab = this.currentTab?.name
      if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
        current_tab = "Portfolio"
      }
      if (this.$store.state.ui.side_menu_open) {
        if (current_tab == item.name) {
          return this.current_theme.bg_color
        }
        if (this.hovered_item == item.name) {
          return this.current_theme.selected_item_color
        }
      } else {
        if (current_tab == item.name) {
          return this.current_theme.selected_item_color
        }
        if (this.hovered_item == item.name) {
          return this.current_theme.selected_item_color
        }
      }
    },

    getTwBgColor(item) {
      let current_tab = this.currentTab?.name
      if (this.$route.name == "CompanyProfile" || this.$route.name == "ObjectProfile") {
        current_tab = "Portfolio"
      }

      if (this.$store.state.ui.side_menu_open) {
        if (current_tab == item.name) {
          return this.current_theme.bg_color
        }
        if (this.hovered_item == item.name) {
          return this.current_theme.selected_item_color
        }
      } else {
        if (current_tab == item.name) {
          return this.current_theme.selected_item_color
        }
        if (this.hovered_item == item.name) {
          return this.current_theme.selected_item_color
        }
      }
    },
    // getIcon(object_type) {
    //   if (object_type == "company") {
    //     return CompanyIcon
    //   }
    //   if (object_type == "opportunity") {
    //     return StarIcon
    //   }
    // },

    extractAllJourneys() {
      const all_journeys = []
      Object.keys(this.journeys_objects).forEach((o) => {
        this.journeys_objects[o].forEach((j) => {
          all_journeys.push(j)
        })
      })
      return all_journeys
    },
    distributeJourneys(journeys) {
      const journeys_objects = {}
      journeys.forEach((journey) => {
        if (journeys_objects[journey.type]) {
          journeys_objects[journey.type].push(journey)
        } else {
          journeys_objects[journey.type] = [journey]
        }
      })
      this.journeys_objects = journeys_objects
    },
    hasPopupMenu(item) {
      let pop_up_menu_available = false
      pop_up_menu_available = item?.children?.filter((c) => c?.is_visible && c?.is_activated && c?.entry_point == "parent")?.length
      if (item?.name === "Customer Journeys" && this.extractAllJourneys().length) {
        pop_up_menu_available = true
      }
      return pop_up_menu_available
    },
    handleHoverSideMenu(show_side_menu) {
      if (this.side_menu_available && !this.$store.state.ui.is_sidebar_collapsed) {
        this.side_menu_open_calc = show_side_menu
      }
    },

    init_realtime_listener() {
      this.$store.dispatch("realtime/connect")
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          this.$store.dispatch("updateLiveStatus", { status: "closed" }, { root: true })
        } else {
          this.$store.dispatch("updateLiveStatus", { status: this.$store.state.realtime.is_connected ? "on" : "off" }, { root: true })
        }
      })
    },

    handleThemeChange() {
      const darkMode = this.$store.state.ui.darkMode
      window.localStorage.setItem("dark-mode", darkMode ? false : true)
      this.$store.dispatch("ui/toggleTheme")
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.OPEN_TEAM_CHAT, () => {
        this.show_team_chat = !this.show_team_chat
      })

      this.$emitter.on(ALL_EMITTERS.JOURNEY_ADDED, (journey) => {
        dataService.getJourneys({ page_size: 100 }).then((res) => {
          this.distributeJourneys(res.data.results)
          this.$router.push(`/expansion/journeys?journey=${journey.id}&object=${journey.type}`)
        })
      })

      this.$emitter.on(ALL_EMITTERS.UPDATED_JOURNEY_IN_SIDE_MENU, (journey) => {
        const all_journeys = this.extractAllJourneys()
        const index = all_journeys.findIndex((j) => j.id == journey.id)
        if (index !== -1) {
          all_journeys.splice(index, 1, journey)
        }

        this.distributeJourneys(all_journeys)
      })

      this.$emitter.on(ALL_EMITTERS.JOURNEY_DELTED, (journey) => {
        dataService.deleteJourney(journey.id).then(() => {
          dataService.getJourneys({ page_size: 100 }).then((res) => {
            this.distributeJourneys(res.data.results)
            setTimeout(() => {
              this.$emitter.emit(ALL_EMITTERS.JOURNEY_DELETED_AND_DISTRIBUTION_DONE_IN_CUSTOMER_JOURENY_VIEW, journey)
              this.$emitter.emit(ALL_EMITTERS.JOURNEY_DELETED_AND_DISTRIBUTION_DONE_IN_JOURNEYS, journey)
            }, 100)
          })
        })
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.OPEN_TEAM_CHAT)
      this.$emitter.off(ALL_EMITTERS.JOURNEY_ADDED)
      this.$emitter.off(ALL_EMITTERS.UPDATED_JOURNEY_IN_SIDE_MENU)
      this.$emitter.off(ALL_EMITTERS.JOURNEY_DELTED)
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },

    handleLogout() {
      google_event("logout", {
        user_name: this.$store.state.auth.user.full_name,
        user_email: this.$store.state.auth.user.email,
        organization: this.$store.state.organization.name
      })
      this.$store.dispatch("auth/logout", this.$router)
    },
    changeLeftSideTab(item, tab) {
      // if user is on the same tab, do nothing

      if (item.action == "switch-to-dark-mode") {
        this.handleThemeChange()
      } else if (item.action == "billing") {
        this.$router.push(item.href)
      } else if (item.href == "https://success.revsetter.com") {
        window.open(item.href, "_blank")
        return
      } else if (item.action == "sign-out") {
        this.handleLogout()
      } else if (item.action == "change-organization") {
        this.$emitter.emit(ALL_EMITTERS.CHANGE_WORKSPACE_ORGANIZATION)
      }
      if (item.action == "share-feedback") {
        this.$emitter.emit(ALL_EMITTERS.FEEDBACK_FORM)
      } else {
        if (this.current_item && this.current_item?.name == item.name && !tab) {
          return
        }

        if (!this.$store.state.ui.layout_edit_mode_active) {
          this.current_item = { ...item }
        }

        this.navigation = this.navigation.map((i) => {
          if (i.name == item.name) {
            i.current = true
          } else {
            i.current = false
          }
          return i
        })
        this.$store.dispatch("ui/setCurrentSelectedTab", item.name)
        if (item.href != null) {
          if (tab) {
            if (item.name == "Customer Journeys") {
              this.$router.push(`${item.href}?journey=${tab.id}&object=${tab.type}`)
            } else {
              this.$router.push(`${item.href}?tab=${tab.name.split(" ")[0].toLowerCase()}`)
            }
          } else {
            this.$router.push(item.href)
          }
        } else {
          if (item && tab && item.name == "Customer Journeys") {
            this.$router.push(`/expansion/journeys?journey=${tab.id}&object=${tab.type}`)
          } else if (item && tab && item.name == "Prospecting") {
            this.$router.push(`/prospecting?tab=${tab.name.split(" ")[0].toLowerCase()}`)
          }
        }
      }
    },
    onDragEnd(_event) {
      // Update the order of the navigation array based on the dragged items
      this.navigation = [...this.draggable_navigation]
      // You might want to save this new order to your store or backend here
      // this.$store.dispatch('control/updateNavigationOrder', new_navigation);
    }
  }
}
</script>
